import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { ApolloError, useLazyQuery } from '@apollo/client';
import { ReactComponent as FacebookAdsIcon } from 'src/icons/facebook.svg';
import { ReactComponent as GoogleAdsIcon } from 'src/icons/googleAds.svg';
import { ReactComponent as GoogleAnalyticsIcon } from 'src/icons/googleAnalytics.svg';
import { ReactComponent as ShopifyIcon } from 'src/icons/shopify.svg';
import { ReactComponent as MagentoIcon } from 'src/icons/magento.svg';
import { ReactComponent as GoogleSearchConsoleIcon } from 'src/icons/googleSearchConsole.svg';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import {
  GET_FACEBOOK_METRICS,
  GET_GOOGLE_ADS_METRICS,
  GET_GOOGLE_ANALYTICS_METRICS,
  GET_GOOGLE_SEARCH_CONSOLE_METRICS,
  GET_SHOPIFY_METRICS,
  GET_MAGENTO_METRICS,
  GET_DATABASE_METRICS_FOR_CUSTOM_REPORT,
} from 'src/utils/gql/queries';
import { ConnectorType, Metric, Query } from 'src/utils/gql/types';
import { useConnectors } from 'src/providers/connectors/hook/useConnectors';

interface ConnectorAccountDetails {
  icon: ReactNode;
  title: string;
}

type ConnectorAccount = {
  [K in ConnectorType]: ConnectorAccountDetails;
};

const conectorAccountData: ConnectorAccount = {
  [ConnectorType.FACEBOOK]: {
    icon: <FacebookAdsIcon />,
    title: 'Facebook Ads',
  },
  [ConnectorType.GOOGLE_ADS]: {
    icon: <GoogleAdsIcon />,
    title: 'Google Ads',
  },
  [ConnectorType.GOOGlE_ANALYTICS4]: {
    icon: <GoogleAnalyticsIcon />,
    title: 'Google Analytics 4',
  },
  [ConnectorType.GOOGLE_SEARCH_CONSOLE]: {
    icon: <GoogleSearchConsoleIcon />,
    title: 'Google Search Console',
  },
  [ConnectorType.SHOPIFY]: {
    icon: <ShopifyIcon />,
    title: 'Shopify',
  },
  [ConnectorType.KIPIFY]: {
    icon: (
      <div
        style={{
          width: '20px',
          height: '20px',
          backgroundColor: '#222822',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#1EED9F',
          fontSize: '12px',
          fontWeight: 'bold',
        }}
      >
        K
      </div>
    ),
    title: 'Kipify',
  },
  [ConnectorType.DATABASE]: {
    icon: (
      <div
        style={{
          width: '20px',
          height: '20px',
          backgroundColor: '#1976d2',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '12px',
          fontWeight: 'bold',
        }}
      >
        DB
      </div>
    ),
    title: 'Database',
  },
  [ConnectorType.MAGENTO]: {
    icon: <MagentoIcon width={20} height={20} />,
    title: 'Magento',
  },
};

export const useMetrics = ({ type }: { type: ConnectorType }) => {
  const snackbar = useSnackbar();
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const { databaseConnectorId, databaseMetrics, magentoConnectorEnabled } =
    useConnectors();

  const onError = (err: ApolloError) => {
    const { graphQLErrors, message: errorText } = err;
    const message =
      graphQLErrors && graphQLErrors.length > 0
        ? graphQLErrors[0].message
        : errorText;

    snackbar(message);
  };

  const [getFacebookMetrics, { loading: gettingFacebookMetrics }] =
    useLazyQuery<Pick<Query, 'getFacebookMetrics'>, never>(
      GET_FACEBOOK_METRICS,
      {
        onCompleted: ({ getFacebookMetrics }) =>
          setMetrics(getFacebookMetrics.metrics),
        onError,
      },
    );

  const [getGoogleAdsMetrics, { loading: gettingGoogleAdsMetrics }] =
    useLazyQuery<Pick<Query, 'getGoogleAdsMetricsList'>, never>(
      GET_GOOGLE_ADS_METRICS,
      {
        onCompleted: ({ getGoogleAdsMetricsList }) =>
          setMetrics(getGoogleAdsMetricsList.metrics),
        onError,
      },
    );

  const [
    getGoogleAnalyticsMetrics,
    { loading: gettingGoogleAnalyticsMetrics },
  ] = useLazyQuery<Pick<Query, 'getGoogleAnalyticsMetrics'>, never>(
    GET_GOOGLE_ANALYTICS_METRICS,
    {
      onCompleted: ({ getGoogleAnalyticsMetrics }) =>
        setMetrics(getGoogleAnalyticsMetrics.metrics),
      onError,
    },
  );

  const [
    getGoogleSearchConsoleMetrics,
    { loading: gettingGoogleSearchConsoleMetrics },
  ] = useLazyQuery<Pick<Query, 'getGoogleSearchConsoleMetrics'>, never>(
    GET_GOOGLE_SEARCH_CONSOLE_METRICS,
    {
      onCompleted: ({ getGoogleSearchConsoleMetrics }) =>
        setMetrics(getGoogleSearchConsoleMetrics.metrics),
      onError,
    },
  );

  const [getShopifyMetrics, { loading: gettingShopifyMetrics }] = useLazyQuery<
    Pick<Query, 'getShopifyMetrics'>,
    never
  >(GET_SHOPIFY_METRICS, {
    // Evita cache stale nella tendina metriche (es. nuove metriche Shopify).
    fetchPolicy: 'cache-and-network',
    onCompleted: ({ getShopifyMetrics }) =>
      setMetrics(
        getShopifyMetrics.metrics.filter(
          (metric) => metric.label !== 'Customers',
        ),
      ),
    onError,
  });

  const [getMagentoMetrics, { loading: gettingMagentoMetrics }] = useLazyQuery<
    Pick<Query, 'getMagentoMetrics'>,
    never
  >(GET_MAGENTO_METRICS, {
    fetchPolicy: 'cache-and-network',
    onCompleted: ({ getMagentoMetrics }) =>
      setMetrics(getMagentoMetrics.metrics),
    onError,
  });

  // Database metrics for Custom Report
  const [getDatabaseMetrics, { loading: gettingDatabaseMetrics }] =
    useLazyQuery(GET_DATABASE_METRICS_FOR_CUSTOM_REPORT, {
      variables: { connectorId: databaseConnectorId || '' },
      onCompleted: ({ getDatabaseConnectorMetricsForCustomReport }) => {
        const dbMetrics = (
          getDatabaseConnectorMetricsForCustomReport || []
        ).map((m: any) => ({
          key: m.key,
          label: m.name,
          category: 'metric',
          id: m.key,
          type: ConnectorType.DATABASE,
          valueType: 'number',
        }));
        setMetrics(dbMetrics);
      },
      onError,
    });

  let loading = false;

  switch (type) {
    case ConnectorType.FACEBOOK:
      loading = gettingFacebookMetrics;

      break;
    case ConnectorType.GOOGLE_ADS:
      loading = gettingGoogleAdsMetrics;

      break;
    case ConnectorType.GOOGlE_ANALYTICS4:
      loading = gettingGoogleAnalyticsMetrics;

      break;
    case ConnectorType.GOOGLE_SEARCH_CONSOLE:
      loading = gettingGoogleSearchConsoleMetrics;

      break;
    case ConnectorType.SHOPIFY:
      loading = gettingShopifyMetrics;

      break;
    case ConnectorType.MAGENTO:
      loading = gettingMagentoMetrics;
      break;
    case ConnectorType.DATABASE:
      loading = gettingDatabaseMetrics;
      break;
  }

  useEffect(() => {
    switch (type) {
      case ConnectorType.FACEBOOK:
        getFacebookMetrics();

        break;
      case ConnectorType.GOOGLE_ADS:
        getGoogleAdsMetrics();

        break;
      case ConnectorType.GOOGlE_ANALYTICS4:
        getGoogleAnalyticsMetrics();

        break;
      case ConnectorType.GOOGLE_SEARCH_CONSOLE:
        getGoogleSearchConsoleMetrics();

        break;
      case ConnectorType.SHOPIFY:
        getShopifyMetrics();

        break;
      case ConnectorType.MAGENTO:
        if (magentoConnectorEnabled) {
          getMagentoMetrics();
        } else {
          setMetrics([]);
        }
        break;
      case ConnectorType.DATABASE:
        // Se abbiamo già metriche da context, usiamole subito
        if (Array.isArray(databaseMetrics) && databaseMetrics.length > 0) {
          setMetrics(databaseMetrics as any);
        } else if (databaseConnectorId) {
          getDatabaseMetrics();
        } else {
          setMetrics([]);
        }
        break;
    }
  }, [
    getFacebookMetrics,
    getGoogleAdsMetrics,
    getGoogleAnalyticsMetrics,
    getGoogleSearchConsoleMetrics,
    getShopifyMetrics,
    getMagentoMetrics,
    getDatabaseMetrics,
    databaseConnectorId,
    databaseMetrics,
    magentoConnectorEnabled,
    type,
  ]);

  const connectorDetails = useMemo(() => conectorAccountData[type], [type]);

  return {
    metrics,
    loading,
    connectorDetails,
  };
};
